import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useApi } from './useApi';

/**
 * Per-user JSON blob storage — read + write + debounced auto-save.
 *
 * Returns:
 *   {
 *     data,            // current value, null until first GET completes
 *     isLoading,
 *     error,
 *     refetch,         // re-run the GET (used by UserBlobErrorBanner Retry)
 *     isFetching,      // true while GET is in flight (incl. refetch)
 *     save(value),     // immediate (non-debounced) save
 *     scheduleSave(v), // debounced 1s save (use for textarea/onChange)
 *   }
 *
 * Use this for pages with multiple localStorage keys per user where a
 * dedicated table would be overkill. CapacityReview / DR are examples.
 *
 * Apply Phase 2 lessons: stable mutateRef, lastSavedJsonRef no-op guard.
 */
export function useUserBlob(key) {
  const apiFetch = useApi();

  const query = useQuery({
    queryKey: ['user-blob', key],
    queryFn: async () => {
      const res = await apiFetch(`/user-blob/${key}`);
      if (res.status === 404) return null;
      if (!res.ok) throw new Error(`Failed to load user-blob '${key}': ${res.status}`);
      const body = await res.json();
      return body.value;
    },
    refetchOnMount: 'always',
    // Don't auto-retry on hard failures — surface them so the user can act
    // (reload, re-auth). React Query default 3× retry hides errors for ~6s.
    // Without this, hydration silently fails and consumer pages' save
    // effects short-circuit forever, with no visible feedback. See the
    // Phase 14 CRM.jsx fix for the canonical pattern; this is the
    // equivalent for blob-pattern pages.
    retry: false,
  });

  const mutation = useMutation({
    mutationFn: async (value) => {
      const res = await apiFetch(`/user-blob/${key}`, {
        method: 'PUT',
        body: JSON.stringify(value),
      });
      if (!res.ok) {
        // Capture the body for diagnosis — bare status codes hide useful detail
        const body = await res.text().catch(() => '');
        throw new Error(`Save failed for '${key}': HTTP ${res.status} ${body.slice(0, 200)}`);
      }
      return res.json();
    },
    onError: (err) => {
      // Don't let mutation errors disappear silently. Log to console so
      // a failing save is at least visible in DevTools.
      // eslint-disable-next-line no-console
      console.error('[useUserBlob] save error', { key, err });
    },
  });

  // Stable refs (avoid identity-loop bug from Phase 2)
  const mutateRef = useRef(mutation.mutate);
  mutateRef.current = mutation.mutate;
  const lastSavedJsonRef = useRef(null);
  const saveTimer = useRef(null);

  // Capture initial server data into lastSavedJsonRef so the first save
  // after hydration doesn't fire if value is unchanged.
  useEffect(() => {
    if (query.data !== undefined && lastSavedJsonRef.current === null) {
      lastSavedJsonRef.current = JSON.stringify(query.data);
    }
  }, [query.data]);

  function save(value) {
    const json = JSON.stringify(value);
    if (json === lastSavedJsonRef.current) return;
    lastSavedJsonRef.current = json;
    if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }
    mutateRef.current(value);
  }

  function scheduleSave(value) {
    const json = JSON.stringify(value);
    if (json === lastSavedJsonRef.current) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      lastSavedJsonRef.current = json;
      mutateRef.current(value);
    }, 1000);
  }

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    isFetching: query.isFetching,
    save,
    scheduleSave,
  };
}

/**
 * Banner to render above a page's main content when its userBlob failed to
 * load. Without this, users see the page's default state, edit it, and
 * silently lose work because the page's save effect short-circuits while
 * hydration hasn't completed.
 *
 * Pass `noun` to phrase the message correctly (e.g. "pricing", "DR plan").
 * Pass `hydrated` to control when the banner hides — typically pages keep
 * their own `hasHydrated` / `hydratedRef` that flips on first successful
 * load; pass it through so the banner disappears the moment the user clicks
 * Retry and the GET succeeds.
 */
export function UserBlobErrorBanner({ blob, noun, hydrated }) {
  if (!blob?.error || hydrated) return null;
  return (
    <div style={{ background: 'var(--danger-soft)', border: '1px solid var(--danger-border)', borderRadius: 'var(--radius)', color: 'var(--danger)', padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
      <div style={{ fontSize: 13 }}>
        <strong>Could not load your {noun || 'data'}.</strong>{' '}
        <span>Any edits you make below will <u>not be saved</u> until this loads successfully.</span>
        <div style={{ fontSize: 11, marginTop: 4, opacity: 0.85 }}>
          {String(blob.error?.message || blob.error).slice(0, 200)}
        </div>
      </div>
      <button onClick={() => blob.refetch?.()} disabled={blob.isFetching}
        style={{ background: 'var(--bg-card)', border: '1px solid var(--danger)', color: 'var(--danger)', borderRadius: 'var(--radius)', padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: blob.isFetching ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}>
        {blob.isFetching ? 'Retrying…' : 'Retry'}
      </button>
    </div>
  );
}

/**
 * Drop-in replacement for useState that also persists to commercial.UserBlob.
 *
 * Returns [value, setValue, isLoading, blob, hydrated].
 * - First three: backward-compatible with the original 3-tuple signature.
 * - `blob` and `hydrated` (added Phase 15) let consumers render
 *   <UserBlobErrorBanner blob={blob} hydrated={hydrated} /> when GET fails.
 *
 * Reads /api/user-blob/{key} on mount; if no DB row, calls `legacyLoader()`
 * (one-shot localStorage migration); otherwise uses `defaultValue`.
 *
 * Writes are debounced 1s and skipped if value didn't actually change.
 *
 * Usage:
 *   const [people, setPeople, , peopleBlob, peopleHydrated] = useDbState(
 *     'cap-review/roles/v1', DEFAULT_ROLES, () => loadFromLocalStorage());
 */
export function useDbState(key, defaultValue, legacyLoader) {
  const blob = useUserBlob(key);
  const [value, setValue] = useState(defaultValue);
  const hydratedRef = useRef(false);

  useEffect(() => {
    if (hydratedRef.current || blob.data === undefined) return;
    hydratedRef.current = true;
    if (blob.data !== null) {
      setValue(blob.data);
      return;
    }
    if (legacyLoader) {
      const legacy = legacyLoader();
      if (legacy != null) {
        setValue(legacy);
        blob.save(legacy);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blob.data]);

  useEffect(() => {
    if (!hydratedRef.current) return;
    blob.scheduleSave(value);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return [value, setValue, blob.isLoading, blob, hydratedRef.current];
}
